<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-6">
    <Card>
      <CardHeader>
        <CardTitle>Import Email Campaign Contacts</CardTitle>
      </CardHeader>
      <CardContent class="flex flex-col gap-6">
        <div class="flex flex-col gap-4">
          <Field>
            <FieldLabel for="email-campaign-id">
              Email Campaign ID (optional)
            </FieldLabel>
            <Input
              id="email-campaign-id"
              v-model="emailCampaignId"
              type="number"
              min="1"
              placeholder="e.g. 42"
            />
            <FieldDescription>
              Leave blank to upload contacts without assigning them to a campaign yet.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel for="contacts-csv">
              Contacts CSV
            </FieldLabel>
            <input
              id="contacts-csv"
              type="file"
              accept=".csv,text/csv"
              class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              @change="handleFileSelection"
            >
            <FieldDescription>
              Upload a CSV with headers like `email`, `first_name`, and `last_name`.
            </FieldDescription>
          </Field>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <Button :disabled="isSubmitDisabled" @click="submitImport">
            <span v-if="isSubmitting">Uploading...</span>
            <span v-else>Upload Contacts</span>
          </Button>
          <p v-if="selectedFile" class="text-sm text-muted-foreground">
            Selected: {{ selectedFile.name }}
          </p>
        </div>

        <div v-if="statusMessage" class="text-sm text-muted-foreground">
          {{ statusMessage }}
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import axios from 'axios'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../resources/js/components/ui/card/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../resources/js/components/ui/input/index.js'

const emailCampaignId = ref('')
const selectedFile = ref(null)
const isSubmitting = ref(false)
const statusMessage = ref('')

const isSubmitDisabled = computed(() => {
  return isSubmitting.value || !selectedFile.value
})

const handleFileSelection = (event) => {
  const file = event.target?.files?.[0] ?? null
  event.target.value = ''

  if (!file) {
    selectedFile.value = null
    return
  }

  if (!file.name.toLowerCase().endsWith('.csv')) {
    toast.error('Please select a CSV file.')
    selectedFile.value = null
    return
  }

  selectedFile.value = file
}

const submitImport = async () => {
  if (isSubmitDisabled.value) {
    toast.error('Select a campaign and CSV file first.')
    return
  }

  const formData = new FormData()
  formData.append('file', selectedFile.value)
  if (emailCampaignId.value !== '') {
    formData.append('email_campaign_id', emailCampaignId.value)
  }

  isSubmitting.value = true
  statusMessage.value = 'Uploading contacts...'

  try {
    const { data } = await axios.post('/api/v1/email-campaign-contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    statusMessage.value = `Upload complete. Upload ID: ${data?.upload?.id ?? 'n/a'}.`
    toast.success('Contacts uploaded. Import processing has started.')
    selectedFile.value = null
  } catch (error) {
    const message = error?.response?.data?.message
      || error?.response?.data?.errors?.file?.[0]
      || 'Unable to upload contacts.'

    statusMessage.value = message
    toast.error(message)
  } finally {
    isSubmitting.value = false
  }
}
</script>
